import { Alert, BootstrapVariant, LogMethod } from '@/utils'
import { getApp } from 'firebase/app'
import { DocumentReference, addDoc, collection, deleteDoc, doc, getDoc, getDocs, getFirestore, limit, orderBy, query, startAfter, startAt, updateDoc, where } from 'firebase/firestore'
import { CACHE, SET_SETTINGS } from './mutations'

export default {
  namespaced: true,
  state: {
    cache: {},
    settings: undefined,
  },
  getters: {},
  mutations: {
    [CACHE](state, payload) {
      state.cache[payload.path] = payload.snapshot
    },
    [SET_SETTINGS](state, payload) {
      state.settings = payload
    },
  },
  actions: {
    async initializeModule({ commit, dispatch }) {
      try {
        const settings = await dispatch(
          'firestore/getDoc',
          {
            path: 'settings/5Es66C3xpqgxT8doWnc1',
          },
          { root: true }
        )
        commit(SET_SETTINGS, settings)
      } catch (error) {
        console.error('firestore module did not initialize correctly.')
      }
    },
    publish({ dispatch }) {
      return new Promise((resolve, reject) => {
        let cache = {}
        let values = []
        dispatch('getDocs', { path: '_blueprints' }).then((documents) => {
          documents.forEach((blueprint) => {
            if (blueprint.cache) {
              values.push(
                dispatch('getDocs', { path: blueprint.collection }).then((documents) => {
                  cache[blueprint.collection] = []
                  documents.forEach((document) => {
                    // TODO change to a filter function
                    if ((document.status == undefined || document.status == true) && (document.available == undefined || document.available == true)) {
                      // TODO change from status to "_draft"
                      cache[blueprint.collection].push(document)
                    }
                  })
                })
              )
            }
          })
          Promise.all(values)
            .then(() => {
              return dispatch('updateDoc', { path: '_cache/CoGEnpNIVuexy5SNhojT2', data: cache })
            })
            .then(async () => {
              await dispatch('report/log', { message: 'Publish completed.', logMethod: LogMethod.Info }, { root: true })
              await dispatch('report/alert', { alert: new Alert(3, true, BootstrapVariant.Success, 'Publish completed.') }, { root: true })
              resolve()
            })
            .catch(async (reason) => {
              await dispatch('report/log', { message: reason, logMethod: LogMethod.Error }, { root: true })
              await dispatch('report/alert', { alert: new Alert(3, true, BootstrapVariant.Danger, 'Something went wrong. Please refresh your browser and try again.') }, { root: true })
              reject()
            })
        })
      })
    },
    getDoc({ commit, dispatch, state }, { app, path }) {
      return new Promise((resolve, reject) => {
        if (!path) {
          return resolve({})
        }
        if (state.cache[path]) {
          resolve({ ...state.cache[path] })
        } else {
          let reference = doc(getFirestore(getApp(app || 'secondary')), path)
          getDoc(reference)
            .then((documentSnapshot) => {
              if (documentSnapshot.exists) {
                let document = {
                  ...documentSnapshot.data(),
                  _reference: reference,
                }
                commit(CACHE, {
                  path: path,
                  snapshot: { ...document },
                })
                resolve(document)
              } else {
                resolve({})
              }
            })
            .catch((reason) => {
              dispatch('report/log', { source: 'firestore/getDoc', message: reason, logMethod: LogMethod.Error }, { root: true })
              reject()
            })
        }
      })
    },
    getDocs({ commit, dispatch, state }, { app, path }) {
      return new Promise((resolve, reject) => {
        if (state.cache[path]) {
          resolve([...state.cache[path]])
        } else {
          getDocs(collection(getFirestore(getApp(app || 'secondary')), path))
            .then((querySnapshot) => {
              let documents = []
              querySnapshot.forEach((queryDocumentSnapshot) => {
                documents.push({
                  ...queryDocumentSnapshot.data(),
                  _reference: doc(getFirestore(getApp(app || 'secondary')), `${path}/${queryDocumentSnapshot.id}`),
                })
              })
              commit(CACHE, {
                path: path,
                snapshot: [...documents],
              })
              resolve(documents)
            })
            .catch((reason) => {
              dispatch('report/log', { source: 'firestore/getDocs', message: reason, logMethod: LogMethod.Error }, { root: true })
              reject()
            })
        }
      })
    },
    getDocsBySearchQuery({ dispatch }, { app, path, search_query }) {
      console.log('getDocsBySearchQuery', path, search_query)
      return new Promise((resolve, reject) => {
        const queryRef = query(collection(getFirestore(getApp(app || 'secondary')), path), where('__search_query', '>=', search_query), where('__search_query', '<=', search_query + '\uf8ff'))
        getDocs(queryRef)
          .then((querySnapshot) => {
            console.log('querySnapshot', querySnapshot)
            let documents = []
            querySnapshot.forEach((queryDocumentSnapshot) => {
              documents.push({
                ...queryDocumentSnapshot.data(),
                _reference: doc(getFirestore(getApp(app || 'secondary')), `${path}/${queryDocumentSnapshot.id}`),
              })
            })
            resolve(documents)
          })
          .catch((reason) => {
            dispatch('report/log', { source: 'firestore/getDocsBySearchQuery', message: reason, logMethod: LogMethod.Error }, { root: true })
            reject()
          })
      })
    },
    collection({ dispatch }, { app, path, settings = {}, currentPage = 1, pages = [] }) {
      return new Promise((resolve, reject) => {
        settings.limit ||= 5
        let result = {}
        dispatch('getDoc', {
          path: '_collections/' + path,
        })
          .then((documentSnapshot) => {
            result.totalRows = documentSnapshot.totalRows
            result.currentPage = currentPage
            let _query = query(collection(getFirestore(getApp(app || 'secondary')), path))
            if (settings.orderBy) {
              _query = query(_query, orderBy(settings.orderBy, 'desc'))
            }
            for (let i = 0; i < settings.filters?.length; i++) {
              _query = query(_query, where(settings.filters[i].fieldPath, settings.filters[i].opStr, settings.filters[i].value))
              console.log('filter', settings.filters[i].fieldPath, settings.filters[i].opStr, settings.filters[i].value)
            }
            if (currentPage != 1) {
              if (settings.startAfter) {
                _query = query(_query, startAfter(settings.startAfter))
              } else {
                _query = query(_query, startAt(pages[currentPage].startAt))
              }
            }
            _query = query(_query, limit(settings.limit))
            return getDocs(_query)
          })
          .then((querySnapshot) => {
            //the last document of current page (ie. the start after for next page)
            settings.startAfter = querySnapshot.docs[querySnapshot.docs.length - 1]

            if (settings.limit * currentPage >= result.totalRows) {
              result.next = null
            } else {
              result.next = function () {
                pages[currentPage] ||= {
                  startAt: querySnapshot.docs[0],
                }
                return dispatch('collection', { app, path, settings, currentPage: ++currentPage, pages })
              }
            }
            if (currentPage == 1) {
              result.previous = null
            } else {
              result.previous = function () {
                settings.startAfter = null
                return dispatch('collection', { app, path, settings, currentPage: --currentPage, pages })
              }
            }
            result.docs = []
            querySnapshot.forEach((queryDocumentSnapshot) => {
              result.docs.push({
                ...queryDocumentSnapshot.data(),
                _reference: doc(getFirestore(getApp(app || 'secondary')), `${path}/${queryDocumentSnapshot.id}`),
              })
            })
            resolve(result)
          })
          .catch(reject)
      })
    },
    updateDoc({ dispatch }, { app, path, data }) {
      return new Promise((resolve, reject) => {
        updateDoc(doc(getFirestore(getApp(app || 'secondary')), path), data)
          .then(resolve)
          .catch((reason) => {
            dispatch('report/log', { source: 'firestore/updateDoc', message: reason, logMethod: LogMethod.Error }, { root: true })
            reject()
          })
      })
    },
    updateDocs({ dispatch }, { app, docs }) {
      return new Promise((resolve, reject) => {
        let values = []
        docs.forEach((element) => {
          values.push(dispatch('updateDoc', { app, path: element.path, data: element.data }))
        })
        Promise.all(values)
          .then(resolve)
          .catch((reason) => {
            dispatch('report/log', { source: 'firestore/updateDocs', message: reason, logMethod: LogMethod.Error }, { root: true })
            reject()
          })
      })
    },
    addDoc({ dispatch }, { app, path, data }) {
      return new Promise((resolve, reject) => {
        addDoc(collection(getFirestore(getApp(app || 'secondary')), path), data)
          .then(resolve)
          .catch((reason) => {
            dispatch('report/log', { source: 'firestore/addDoc', message: reason, logMethod: LogMethod.Error }, { root: true })
            reject()
          })
      })
    },
    addDocs({ dispatch }, { app, docs }) {
      return new Promise((resolve, reject) => {
        let values = []
        docs.forEach((element) => {
          values.push(dispatch('addDoc', { app, path: element.path, data: element.data }))
        })
        Promise.all(values)
          .then(resolve)
          .catch((reason) => {
            dispatch('report/log', { source: 'firestore/addDocs', message: reason, logMethod: LogMethod.Error }, { root: true })
            reject()
          })
      })
    },
    deleteDoc({ dispatch }, { app, path }) {
      return new Promise((resolve, reject) => {
        deleteDoc(doc(getFirestore(getApp(app || 'secondary')), path))
          .then(resolve)
          .catch((reason) => {
            dispatch('report/log', { source: 'firestore/deleteDoc', message: reason, logMethod: LogMethod.Error }, { root: true })
            reject()
          })
      })
    },
    deleteDocs({ dispatch }, { app, paths }) {
      return new Promise((resolve, reject) => {
        let values = []
        paths.forEach((path) => {
          values.push(dispatch('deleteDoc', { app, path }))
        })
        Promise.all(values)
          .then(resolve)
          .catch((reason) => {
            dispatch('report/log', { source: 'firestore/deleteDocs', message: reason, logMethod: LogMethod.Error }, { root: true })
            reject()
          })
      })
    },
    populateDoc({ dispatch }, { document }) {
      console.log('populateDoc', document)
      return new Promise((resolve, reject) => {
        const values = []
        for (const [key, value] of Object.entries(document)) {
          if (!key.startsWith('_')) {
            values.push(
              dispatch('populateField', { field: value }).then((result) => {
                document[key] = result
              })
            )
          }
        }
        Promise.all(values)
          .then(() => {
            resolve(document)
          })
          .catch((reason) => {
            dispatch('report/log', { source: 'firestore/populateDoc', message: reason, logMethod: LogMethod.Error }, { root: true })
            reject()
          })
      })
    },
    populateField({ dispatch }, { field }) {
      console.log('populateField', field)
      return new Promise((resolve, reject) => {
        if (field === null || field === undefined) {
          resolve(field)
        } else {
          if (field instanceof DocumentReference) {
            return dispatch('getDoc', {
              path: field.path,
            })
          }
          if (typeof field === 'object') {
            const values = []
            if (!Array.isArray(field)) {
              for (const [key, value] of Object.entries(field)) {
                values.push(
                  dispatch('populateField', { field: value }).then((result) => {
                    field[key] = result
                  })
                )
              }
            } else {
              for (let i = 0; i < field.length; i++) {
                values.push(
                  dispatch('populateField', { field: field[i] }).then((result) => {
                    field[i] = result
                  })
                )
              }
            }
            Promise.all(values)
              .then(() => {
                resolve(field)
              })
              .catch((reason) => {
                dispatch('report/log', { source: 'firestore/populateField', message: reason, logMethod: LogMethod.Error }, { root: true })
                reject()
              })
          }
          resolve(field)
        }
      })
    },
  },
  modules: {},
}
